export interface Question {
  id: number;
  text: string;
  traits?: {
    [key: string]: number;
  };
  mbtiDimension?: {
    dimension: "EI" | "SN" | "TF" | "JP";
    direction: number; // Positive values favor the first letter, negative the second
  };
  questionType?: 'personality' | 'career'; 
}

export interface PersonalityTrait {
  id: string;
  name: string;
  description: string;
}

export interface MBTIType {
  id: string; // ENFP, ISTJ, etc.
  name: string;
  description: string;
  strengths: string[];
  challenges: string[];
  compatibleCareers: string[]; // Career IDs that match well with this type
}

export interface PersonalityType {
  id: string;
  name: string;
  description: string;
  traits: Record<string, number>;
  careerMatches: string[];
}

export const personalityTraits: PersonalityTrait[] = [
  {
    id: "analytical",
    name: "Analytical",
    description: "You enjoy problem-solving and working with complex information."
  },
  {
    id: "creative",
    name: "Creative",
    description: "You think outside the box and enjoy expressing original ideas."
  },
  {
    id: "social",
    name: "Social",
    description: "You thrive in collaborative environments and enjoy helping others."
  },
  {
    id: "practical",
    name: "Practical",
    description: "You prefer hands-on work and tangible results."
  },
  {
    id: "leadership",
    name: "Leadership",
    description: "You enjoy taking charge and influencing directions."
  },
  {
    id: "adaptable",
    name: "Adaptable",
    description: "You're flexible and comfortable with change and uncertainty."
  }
];

export const mbtiTypes: MBTIType[] = [
  {
    id: "INTJ",
    name: "The Architect",
    description: "Strategic, innovative thinkers with a plan for everything. You are analytical and have a talent for seeing possibilities for improvement in almost every area of your life.",
    strengths: ["Strategic thinking", "Independent", "Determined", "Curious", "Rational"],
    challenges: ["Perfectionist", "Dismissive of emotions", "Overly critical", "Judgmental"],
    compatibleCareers: ["aiEthicist", "dataEthicist", "quantumComputingAnalyst", "cybersecuritySpecialist", "softwareEngineer", "analyst"]
  },
  {
    id: "INTP",
    name: "The Logician",
    description: "Innovative inventors with an unquenchable thirst for knowledge. You value precision in your thinking and communication, and have a unique perspective on the world.",
    strengths: ["Analytical", "Original", "Open-minded", "Objective", "Curious"],
    challenges: ["Disconnected", "Insensitive", "Indecisive", "Procrastinating"],
    compatibleCareers: ["quantumComputingAnalyst", "roboticsEngineer", "sustainabilityConsultant", "experienceDesigner", "softwareEngineer", "physician"]
  },
  {
    id: "ENTJ",
    name: "The Commander",
    description: "Bold, imaginative, and strong-willed leaders who always find a way – or make one. You're a natural leader who values knowledge and competence above all.",
    strengths: ["Efficient", "Energetic", "Self-confident", "Strategic", "Charismatic"],
    challenges: ["Impatient", "Stubborn", "Arrogant", "Intolerant"],
    compatibleCareers: ["futureOfWorkStrategist", "innovationConsultant", "telemedicalDirector", "humanAiCollaborationManager", "sustainabilityLeader", "lawyer"]
  },
  {
    id: "ENTP",
    name: "The Debater",
    description: "Smart and curious thinkers who cannot resist an intellectual challenge. You're creative problem-solvers who can think outside the box and make connections others miss.",
    strengths: ["Knowledgeable", "Creative", "Charismatic", "Energetic", "Adaptable"],
    challenges: ["Argumentative", "Insensitive", "Impatient", "Unfocused"],
    compatibleCareers: ["aiContentCreator", "innovationConsultant", "virtualRealityDesigner", "futurist", "entrepreneurialCatalyst", "therapist"]
  },
  {
    id: "INFJ",
    name: "The Advocate",
    description: "Quiet and mystical, yet very inspiring and tireless idealists. You're an idealist with strong values and intuition about people and situations.",
    strengths: ["Creative", "Insightful", "Principled", "Passionate", "Altruistic"],
    challenges: ["Sensitive to criticism", "Perfectionist", "Private", "Burnout-prone"],
    compatibleCareers: ["communityHealthWorker", "digitalEducationSpecialist", "aiEthicist", "regenerativeCareSpecialist", "therapist", "teacher"]
  },
  {
    id: "INFP",
    name: "The Mediator",
    description: "Poetic, kind, and altruistic people, always eager to help a good cause. You're guided by your values and seek to help others while finding your own purpose.",
    strengths: ["Empathetic", "Creative", "Open-minded", "Passionate", "Idealistic"],
    challenges: ["Impractical", "Self-isolating", "Unfocused", "Emotionally vulnerable"],
    compatibleCareers: ["experienceDesigner", "bioartist", "digitalEducationSpecialist", "communityHealthWorker", "therapist", "teacher"]
  },
  {
    id: "ENFJ",
    name: "The Protagonist",
    description: "Charismatic and inspiring leaders, able to mesmerize their listeners. You're warm, empathetic and responsive, keenly attuned to others' motivations and needs.",
    strengths: ["Charismatic", "Empathetic", "Reliable", "Natural leaders", "Motivational"],
    challenges: ["Overly idealistic", "Approval-seeking", "Self-sacrificing", "Overly sensitive"],
    compatibleCareers: ["remoteWorkCoordinator", "digitalEducationSpecialist", "humanAiCollaborationManager", "telemedicalDirector", "teacher", "therapist"]
  },
  {
    id: "ENFP",
    name: "The Campaigner",
    description: "Enthusiastic, creative, and sociable free spirits who can always find a reason to smile. You're curious, energetic and value connection with others.",
    strengths: ["Curious", "Observant", "Energetic", "Enthusiastic", "Excellent communicator"],
    challenges: ["Unfocused", "Disorganized", "People-pleasing", "Restless"],
    compatibleCareers: ["aiContentCreator", "experienceDesigner", "innovationConsultant", "communityHealthWorker", "remoteWorkCoordinator", "teacher"]
  },
  {
    id: "ISTJ",
    name: "The Logistician",
    description: "Practical and fact-minded individuals, whose reliability cannot be doubted. You're dependable, organized and take your responsibilities seriously.",
    strengths: ["Honest", "Direct", "Responsible", "Calm", "Practical"],
    challenges: ["Stubborn", "Insensitive", "Judgmental", "Blame-taking"],
    compatibleCareers: ["cybersecuritySpecialist", "dataEthicist", "renewableEnergyTechnician", "biomedicalTechnician", "electrician", "physician"]
  },
  {
    id: "ISFJ",
    name: "The Defender",
    description: "Very dedicated and warm protectors, always ready to defend their loved ones. You're detail-oriented, reliable and have a strong sense of responsibility to others.",
    strengths: ["Supportive", "Reliable", "Observant", "Hard-working", "Good practical skills"],
    challenges: ["Overworked", "Reluctant to change", "Too humble", "Taking things personally"],
    compatibleCareers: ["communityHealthWorker", "biomedicalTechnician", "predictiveHealthcareAnalyst", "traditionalSkillsPreserver", "nurse", "teacher"]
  },
  {
    id: "ESTJ",
    name: "The Executive",
    description: "Excellent administrators, unsurpassed at managing things or people. You're organized, logical and value tradition, security and stability.",
    strengths: ["Dedicated", "Strong-willed", "Direct", "Loyal", "Patient"],
    challenges: ["Inflexible", "Resistant to change", "Judgmental", "Too focused on social status"],
    compatibleCareers: ["smartCityPlanningSpecialist", "sustainabilityConsultant", "telemedicalDirector", "3dPrintingSpecialist", "lawyer", "electrician"]
  },
  {
    id: "ESFJ",
    name: "The Consul",
    description: "Extraordinarily caring, social and popular people, always eager to help. You're conscientious, sociable and want to make a positive impact on others.",
    strengths: ["Practical", "Loyal", "Sensitive", "Warm", "Good at connecting"],
    challenges: ["Needing approval", "Too selfless", "Inflexible", "Vulnerable to criticism"],
    compatibleCareers: ["remoteWorkCoordinator", "communityHealthWorker", "telemedicalDirector", "experienceDesigner", "nurse", "teacher"]
  },
  {
    id: "ISTP",
    name: "The Virtuoso",
    description: "Bold and practical experimenters, masters of all kinds of tools. You're adaptable problem-solvers who enjoy understanding how things work.",
    strengths: ["Optimistic", "Energetic", "Creative", "Practical", "Spontaneous"],
    challenges: ["Stubbornness", "Insensitivity", "Risk-prone", "Easily bored"],
    compatibleCareers: ["3dPrintingSpecialist", "roboticsEngineer", "renewableEnergyTechnician", "biomedicalTechnician", "electrician", "softwareEngineer"]
  },
  {
    id: "ISFP",
    name: "The Adventurer",
    description: "Flexible and charming artists, always ready to explore and experience something new. You're sensitive, artistic and value your personal space and freedom.",
    strengths: ["Charming", "Sensitive to others", "Imaginative", "Passionate", "Artistic"],
    challenges: ["Fiercely independent", "Unpredictable", "Easily stressed", "Avoidant of conflict"],
    compatibleCareers: ["bioartist", "experienceDesigner", "virtualRealityDesigner", "sustainabilityConsultant", "chef", "therapist"]
  },
  {
    id: "ESTP",
    name: "The Entrepreneur",
    description: "Smart, energetic and very perceptive people, who truly enjoy living on the edge. You're pragmatic, adaptable and enjoy taking action rather than planning.",
    strengths: ["Bold", "Rational", "Practical", "Original", "Perceptive"],
    challenges: ["Impatient", "Risk-prone", "Unstructured", "Blunt"],
    compatibleCareers: ["3dPrintingSpecialist", "smartCityPlanningSpecialist", "innovationConsultant", "experienceDesigner", "sustainabilityConsultant", "chef"]
  },
  {
    id: "ESFP",
    name: "The Entertainer",
    description: "Spontaneous, energetic and enthusiastic people – life is never boring around them. You're social, fun-loving and enjoy being in the spotlight.",
    strengths: ["Bold", "Original", "Practical", "Observant", "Excellent people skills"],
    challenges: ["Easily bored", "Sensitive", "Conflict-averse", "Poor long-term planning"],
    compatibleCareers: ["experienceDesigner", "remoteWorkCoordinator", "aiContentCreator", "virtualRealityDesigner", "chef", "nurse"]
  }
];

export const personalityTypes: PersonalityType[] = [
  {
    id: "innovator",
    name: "The Innovator",
    description: "You thrive on creating new solutions to complex problems. Your natural curiosity and analytical mindset make you perfect for emerging technical fields.",
    traits: {
      analytical: 0.8,
      creative: 0.9,
      social: 0.3,
      practical: 0.5,
      leadership: 0.6,
      adaptable: 0.7
    },
    careerMatches: ["aiEthicist", "virtualRealityDesigner", "roboticsEngineer", "sustainabilityConsultant"]
  },
  {
    id: "connector",
    name: "The Connector",
    description: "You excel at building relationships and facilitating communication between different groups. Your empathy and social intelligence are valuable assets.",
    traits: {
      analytical: 0.4,
      creative: 0.5,
      social: 0.9,
      practical: 0.3,
      leadership: 0.7,
      adaptable: 0.8
    },
    careerMatches: ["communityHealthWorker", "remoteWorkCoordinator", "humanAiCollaborationManager", "digitalEducationSpecialist"]
  },
  {
    id: "builder",
    name: "The Builder",
    description: "You prefer concrete tasks and enjoy seeing tangible progress in your work. Your practical mindset and reliability make you valuable in hands-on roles.",
    traits: {
      analytical: 0.5,
      creative: 0.4,
      social: 0.5,
      practical: 0.9,
      leadership: 0.4,
      adaptable: 0.5
    },
    careerMatches: ["3dPrintingSpecialist", "renewableEnergyTechnician", "smartCityPlanningSpecialist", "biomedicalTechnician"]
  },
  {
    id: "analyst",
    name: "The Analyst",
    description: "You excel at processing complex information and identifying patterns. Your systematic approach and attention to detail are powerful assets.",
    traits: {
      analytical: 0.9,
      creative: 0.5,
      social: 0.3,
      practical: 0.6,
      leadership: 0.4,
      adaptable: 0.5
    },
    careerMatches: ["dataEthicist", "quantumComputingAnalyst", "predictiveHealthcareAnalyst", "cybersecuritySpecialist"]
  },
  {
    id: "catalyst",
    name: "The Catalyst",
    description: "You're naturally skilled at inspiring and mobilizing others toward a vision. Your leadership and adaptability make you effective in driving change.",
    traits: {
      analytical: 0.5,
      creative: 0.7,
      social: 0.7,
      practical: 0.4,
      leadership: 0.9,
      adaptable: 0.8
    },
    careerMatches: ["innovationConsultant", "sustainabilityLeader", "futureOfWorkStrategist", "telemedicalDirector"]
  },
  {
    id: "creator",
    name: "The Creator",
    description: "You thrive on original expression and finding novel solutions. Your imagination and ability to envision new possibilities set you apart.",
    traits: {
      analytical: 0.4,
      creative: 0.9,
      social: 0.5,
      practical: 0.3,
      leadership: 0.5,
      adaptable: 0.7
    },
    careerMatches: ["virtualRealityDesigner", "aiContentCreator", "experienceDesigner", "bioartist"]
  }
];

// Add new careers to cover MBTI connections
export const mbtiCareerExtensions = [
  {
    id: "futurist",
    title: "Strategic Foresight Consultant",
    description: "Analyzes technological, social, and economic trends to help organizations prepare for multiple potential futures.",
    connections: ["ENTP", "INTJ", "ENTJ"]
  },
  {
    id: "entrepreneurialCatalyst",
    title: "Entrepreneurial Ecosystem Developer",
    description: "Creates supportive environments for startups and helps established companies develop entrepreneurial mindsets.",
    connections: ["ENTP", "ENTJ", "ESTP"]
  },
  {
    id: "regenerativeCareSpecialist",
    title: "Regenerative Care Specialist",
    description: "Combines psychological support with sustainable wellbeing practices to help people recover from trauma or burnout.",
    connections: ["INFJ", "ENFJ", "ISFJ"]
  },
  {
    id: "traditionalSkillsPreserver",
    title: "Traditional Skills Preservation Specialist",
    description: "Documents, teaches, and adapts traditional crafts and skills for modern contexts and sustainability.",
    connections: ["ISFJ", "ISTJ", "ISFP"]
  },
  {
    id: "analyst",
    title: "Complex Systems Analyst",
    description: "Interprets patterns in large data sets to understand complex societal, technological, and environmental systems.",
    connections: ["INTJ", "INTP", "ISTJ"]
  }
];

export const questions: Question[] = [
  // MBTI Questions - Extraversion vs Introversion (EI)
  {
    id: 1,
    text: "I prefer spending time with groups of people rather than on my own.",
    mbtiDimension: {
      dimension: "EI",
      direction: 2 // Positive favors Extraversion
    },
    questionType: 'personality'
  },
  {
    id: 2,
    text: "I prefer to think through ideas on my own before sharing them with others.",
    mbtiDimension: {
      dimension: "EI",
      direction: -2 // Negative favors Introversion
    },
    questionType: 'personality'
  },
  {
    id: 3,
    text: "I tend to speak first and think later in discussions.",
    mbtiDimension: {
      dimension: "EI",
      direction: 2 // Positive favors Extraversion
    },
    questionType: 'personality'
  },
  {
    id: 4,
    text: "I get energy from spending quiet time alone.",
    mbtiDimension: {
      dimension: "EI",
      direction: -2 // Negative favors Introversion
    },
    questionType: 'personality'
  },
  {
    id: 5,
    text: "I usually prefer being the center of attention in social situations.",
    mbtiDimension: {
      dimension: "EI",
      direction: 2 // Positive favors Extraversion
    },
    questionType: 'personality'
  },
  
  // Sensing vs Intuition (SN)
  {
    id: 6,
    text: "I focus more on the immediate reality of a situation than on future possibilities.",
    mbtiDimension: {
      dimension: "SN",
      direction: 2 // Positive favors Sensing
    },
    questionType: 'personality'
  },
  {
    id: 7,
    text: "I prefer to focus on big ideas and patterns rather than concrete details.",
    mbtiDimension: {
      dimension: "SN",
      direction: -2 // Negative favors Intuition
    },
    questionType: 'personality'
  },
  {
    id: 8,
    text: "I tend to trust facts and direct experience more than hunches or intuition.",
    mbtiDimension: {
      dimension: "SN",
      direction: 2 // Positive favors Sensing
    },
    questionType: 'personality'
  },
  {
    id: 9,
    text: "I'm more interested in exploring theoretical concepts than practical applications.",
    mbtiDimension: {
      dimension: "SN",
      direction: -2 // Negative favors Intuition
    },
    questionType: 'personality'
  },
  {
    id: 10,
    text: "I prefer step-by-step instructions rather than figuring things out as I go.",
    mbtiDimension: {
      dimension: "SN",
      direction: 2 // Positive favors Sensing
    },
    questionType: 'personality'
  },
  
  // Thinking vs Feeling (TF)
  {
    id: 11,
    text: "I make decisions based more on logic and objective analysis than personal values.",
    mbtiDimension: {
      dimension: "TF",
      direction: 2 // Positive favors Thinking
    },
    questionType: 'personality'
  },
  {
    id: 12,
    text: "I consider how decisions will affect people's feelings before making them.",
    mbtiDimension: {
      dimension: "TF",
      direction: -2 // Negative favors Feeling
    },
    questionType: 'personality'
  },
  {
    id: 13,
    text: "I value justice and fair treatment over maintaining harmony in relationships.",
    mbtiDimension: {
      dimension: "TF",
      direction: 2 // Positive favors Thinking
    },
    questionType: 'personality'
  },
  {
    id: 14,
    text: "I'm good at understanding how others are feeling, even when they don't say it directly.",
    mbtiDimension: {
      dimension: "TF",
      direction: -2 // Negative favors Feeling
    },
    questionType: 'personality'
  },
  {
    id: 15,
    text: "I prefer giving honest feedback over being tactful to avoid hurting feelings.",
    mbtiDimension: {
      dimension: "TF",
      direction: 2 // Positive favors Thinking
    },
    questionType: 'personality'
  },
  
  // Judging vs Perceiving (JP)
  {
    id: 16,
    text: "I prefer having a detailed plan before starting a project.",
    mbtiDimension: {
      dimension: "JP",
      direction: 2 // Positive favors Judging
    },
    questionType: 'personality'
  },
  {
    id: 17,
    text: "I enjoy being spontaneous and adapting to situations as they arise.",
    mbtiDimension: {
      dimension: "JP",
      direction: -2 // Negative favors Perceiving
    },
    questionType: 'personality'
  },
  {
    id: 18,
    text: "I like to have things decided and settled rather than left open-ended.",
    mbtiDimension: {
      dimension: "JP",
      direction: 2 // Positive favors Judging
    },
    questionType: 'personality'
  },
  {
    id: 19,
    text: "I prefer keeping my options open rather than making final decisions quickly.",
    mbtiDimension: {
      dimension: "JP",
      direction: -2 // Negative favors Perceiving
    },
    questionType: 'personality'
  },
  {
    id: 20,
    text: "I feel stressed when deadlines are approaching and work isn't completed well in advance.",
    mbtiDimension: {
      dimension: "JP",
      direction: 2 // Positive favors Judging
    },
    questionType: 'personality'
  },
  
  // Additional career-related questions
  {
    id: 21,
    text: "I'd prefer a career that will be significantly transformed by technology in the next decade.",
    traits: {
      adaptable: 1,
      creative: 1
    },
    questionType: 'career'
  },
  {
    id: 22,
    text: "I'm more interested in established, traditional career paths than emerging ones.",
    traits: {
      practical: 1,
      adaptable: -1
    },
    questionType: 'career'
  },
  {
    id: 23,
    text: "I would enjoy working in a field that doesn't fully exist yet.",
    traits: {
      adaptable: 2,
      creative: 1
    },
    questionType: 'career'
  },
  {
    id: 24,
    text: "I value stability and predictability in my future career.",
    traits: {
      practical: 1,
      adaptable: -1
    },
    questionType: 'career'
  },
  {
    id: 25,
    text: "I'm comfortable with the idea that my job might be significantly different in 5 years from what it is when I start.",
    traits: {
      adaptable: 2
    },
    questionType: 'career'
  },
  {
    id: 26,
    text: "I'd prefer to work with physical materials and tools rather than digital tools and information.",
    traits: {
      practical: 2,
      creative: 0
    },
    questionType: 'career'
  },
  {
    id: 27,
    text: "I'm interested in being part of creating new technologies rather than using existing ones.",
    traits: {
      creative: 2,
      analytical: 1
    },
    questionType: 'career'
  }
];

export interface CareerPreferences {
  traditionalVsEmerging: number; // -5 to 5, negative values favor traditional
  technicalVsHuman: number; // -5 to 5, negative values favor human-centered
  specializedVsAdaptable: number; // -5 to 5, negative values favor specialized
}

export interface MBTIResult {
  type: string; // e.g., "INTJ"
  details: MBTIType;
  dimensionScores: {
    EI: number; // Positive means E, negative means I
    SN: number; // Positive means S, negative means N
    TF: number; // Positive means T, negative means F
    JP: number; // Positive means J, negative means P
  };
}

export const calculatePersonalityType = (
  answers: Record<number, number>
): { 
  personalityType: PersonalityType; 
  careerPreferences: CareerPreferences;
  mbtiResult: MBTIResult;
} => {
  // Initialize trait scores
  const traitScores: Record<string, number> = {
    analytical: 0,
    creative: 0,
    social: 0,
    practical: 0,
    leadership: 0,
    adaptable: 0
  };

  // Initialize MBTI dimension scores
  const mbtiScores = {
    EI: 0, // Positive means E, negative means I
    SN: 0, // Positive means S, negative means N
    TF: 0, // Positive means T, negative means F
    JP: 0  // Positive means J, negative means P
  };

  // Initialize career preferences
  const careerPreferences: CareerPreferences = {
    traditionalVsEmerging: 0,
    technicalVsHuman: 0,
    specializedVsAdaptable: 0
  };

  // Calculate trait scores and MBTI dimensions based on answers
  Object.entries(answers).forEach(([questionId, answerValue]) => {
    const question = questions.find(q => q.id === parseInt(questionId));
    if (question) {
      // Update trait scores if the question has traits
      if (question.traits) {
        Object.entries(question.traits).forEach(([trait, weight]) => {
          traitScores[trait] += weight * answerValue;
        });
      }
      
      // Update MBTI dimension scores if the question has an MBTI dimension
      if (question.mbtiDimension) {
        const { dimension, direction } = question.mbtiDimension;
        mbtiScores[dimension] += direction * answerValue;
      }
      
      // Update career preferences based on career questions
      if (question.questionType === 'career') {
        const qId = parseInt(questionId);
        
        // Questions about traditional vs emerging careers
        if ([20, 21, 22].includes(qId)) {
          // Questions 20 and 22 favor traditional when answered positively
          // Question 21 favors emerging when answered positively
          const factor = (qId === 21) ? 1 : -1;
          careerPreferences.traditionalVsEmerging += answerValue * factor;
        }
        
        // Questions about technical vs human-centered work
        if ([19, 24].includes(qId)) {
          // Question 19 favors technical when answered positively
          // Question 24 favors human-centered when answered positively
          const factor = (qId === 19) ? 1 : -1;
          careerPreferences.technicalVsHuman += answerValue * factor;
        }
        
        // Questions about specialized vs adaptable careers
        if ([23, 25].includes(qId)) {
          // Both questions favor adaptable when answered positively
          careerPreferences.specializedVsAdaptable += answerValue;
        }
      }
    }
  });

  // Normalize scores to range 0-1 for traits
  const maxPossibleScore = 10; // Assuming max possible score per trait
  Object.keys(traitScores).forEach(trait => {
    traitScores[trait] = Math.max(0, Math.min(1, traitScores[trait] / maxPossibleScore));
  });

  // Determine MBTI type
  const mbtiType = 
    (mbtiScores.EI >= 0 ? "E" : "I") +
    (mbtiScores.SN >= 0 ? "S" : "N") +
    (mbtiScores.TF >= 0 ? "T" : "F") +
    (mbtiScores.JP >= 0 ? "J" : "P");
  
  const mbtiDetails = mbtiTypes.find(type => type.id === mbtiType) || mbtiTypes[0];
  
  // Find the personality type with the closest match to trait scores
  let bestMatch = personalityTypes[0];
  let bestMatchScore = 0;

  personalityTypes.forEach(type => {
    let matchScore = 0;
    Object.entries(type.traits).forEach(([trait, value]) => {
      // Calculate how close the user's trait score is to this type's trait score
      matchScore += 1 - Math.abs(traitScores[trait] - value);
    });
    
    if (matchScore > bestMatchScore) {
      bestMatchScore = matchScore;
      bestMatch = type;
    }
  });

  const mbtiResult: MBTIResult = {
    type: mbtiType,
    details: mbtiDetails,
    dimensionScores: mbtiScores
  };

  return { 
    personalityType: bestMatch,
    careerPreferences,
    mbtiResult
  };
};
